import { Component, OnInit } from '@angular/core';
import { PatientPortalService } from 'src/app/core/services/patient-portal.service';
import { Order, OrdersResponse, ApiResponse } from 'src/app/core/models/patientportal';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-my-orders',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './my-orders.component.html',
  styleUrl: './my-orders.component.scss'
})
export class MyOrdersComponent {
  orders: Order[] = [];
  loading = false;
  errorMsg = '';

  pagination = {
    page: 1,
    limit: 10,
    totalPages: 0
  };

  pages: number[] = [];

  selectedOrder: Order | null = null;
  modalOpen = false;
  modalLoading = false;

  trackingModalOpen = false;
  trackingResult: any = null;
  trackingLoading = false;


  /////////////////////////////////////////////////
  query = {
    q: '',
    status: '',
    method: '',
    minTotal: null,
    maxTotal: null,
    page: 1,
    limit: 10,
    sort: 'createdAt:desc'
  };

  total = 0;
  totalPages = 1;

  search$ = new Subject<string>();

  statusOptions = [
    { label: 'All', value: '' },
    { label: 'Processing', value: 'processing' },
    { label: 'Dispatched', value: 'dispatched' },
    { label: 'Delivered', value: 'delivered' },
    { label: 'Cancelled', value: 'cancelled' }
  ];

  sortOptions = [
    { label: 'Newest', value: 'createdAt:desc' },
    { label: 'Oldest', value: 'createdAt:asc' },
    { label: 'Amount High → Low', value: 'grandTotal:desc' },
    { label: 'Amount Low → High', value: 'grandTotal:asc' }
  ];

  onSearch(v: string) {
    this.search$.next(v);
  }

  applyFilters() {
    this.query.page = 1;
    this.fetchOrders();
  }

  resetFilters() {
    this.query = {
      q: '',
      status: '',
      method: '',
      minTotal: null,
      maxTotal: null,
      page: 1,
      limit: 10,
      sort: 'createdAt:desc'
    };
    this.fetchOrders();
  }

  setSort(v: string) {
    this.query.sort = v;
    this.fetchOrders();
  }

  goToPage(p: number) {
    if (p < 1 || p > this.totalPages) return;
    this.query.page = p;
    this.fetchOrders();
  }

  trackingNo = '';
  trackingError = '';

  constructor(private service: PatientPortalService) { }

  ngOnInit(): void {
    this.search$
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(v => {
        this.query.q = v;
        this.query.page = 1;
        this.fetchOrders();
      });

    this.fetchOrders();
  }

  fetchOrders(): void {
    this.loading = true;
    this.errorMsg = '';

    this.service.getMyOrders({
      page: this.pagination.page,
      limit: this.pagination.limit
    }).subscribe({
      next: (res: ApiResponse<OrdersResponse>) => {
        this.orders = res.data.orders || [];
        this.pagination.totalPages = res.data.pagination?.totalPages || 0;

        // generate pages array
        this.pages = Array.from(
          { length: this.pagination.totalPages },
          (_, i) => i + 1
        );

        this.loading = false;
      },
      error: (err) => {
        this.errorMsg = 'Failed to load orders';
        this.loading = false;
      }
    });
  }

  openOrder(id: string) {
    this.modalOpen = true;
    this.modalLoading = true;
    this.selectedOrder = null;

    this.service.getMyOrderById(id).subscribe({
      next: (res) => {
        this.selectedOrder = res.data.order;
        this.modalLoading = false;
      },
      error: () => {
        this.modalLoading = false;
      }
    });
  }

  closeModal() {
    this.modalOpen = false;
  }

  getStepIndex(status: string): number {
    const map: any = {
      'PROCESSING': 0,
      'READY_FOR_DISPATCH': 1,
      'DISPATCHED': 1,
      'DELIVERED': 2
    };

    return map[status] ?? 0;
  }

  formatStatus(status: string): string {
    return status.replace(/_/g, ' ').toLowerCase()
      .replace(/\b\w/g, c => c.toUpperCase());
  }

  getInvoiceClass(status: string) {
    return {
      'paid': status === 'PAID',
      'pending': status === 'PENDING' || status === 'OPEN',
      'failed': status === 'FAILED',
      'refunded': status === 'REFUNDED'
    };
  }

  getInvoiceIcon(status: string) {
    switch (status) {
      case 'PAID': return 'bi-check-circle';
      case 'FAILED': return 'bi-x-circle';
      case 'REFUNDED': return 'bi-arrow-counterclockwise';
      default: return 'bi-clock';
    }
  }

  changePage(page: number): void {
    if (page === this.pagination.page) return;

    this.pagination.page = page;
    this.fetchOrders();
  }

  trackByOrder(index: number, order: Order): string {
    return order._id;
  }

  copiedTracking: string | null = null;

  copyTracking(val: string | undefined) {
    if (!val) return;

    navigator.clipboard.writeText(val);

    this.copiedTracking = val;

    setTimeout(() => {
      this.copiedTracking = null;
    }, 1500);
  }

  trackOrderNow() {
    if (!this.trackingNo) return;

    this.trackingModalOpen = true;
    this.trackingLoading = true;
    this.trackingResult = null;

    this.service.trackOrder(this.trackingNo).subscribe({
      next: (res) => {
        this.trackingResult = res.data;
        this.trackingLoading = false;

        setTimeout(() => {
          this.renderLottie(this.trackingResult.status);
        }, 0);
      },
      error: () => {
        this.trackingLoading = false;
      }
    });
  }

  closeTrackingModal() {
    this.trackingModalOpen = false;
  }

  /* 🔥 LOTTIE RENDER (CDN) */
  renderLottie(status: string) {
    let path = '';

    switch (status) {
      case 'PROCESSING':
        path = 'https://assets10.lottiefiles.com/packages/lf20_j1adxtyb.json';
        break;

      case 'READY_FOR_DISPATCH':
      case 'DISPATCHED':
        path = 'https://assets10.lottiefiles.com/packages/lf20_3rwasyjy.json';
        break;

      case 'DELIVERED':
        path = 'https://assets10.lottiefiles.com/packages/lf20_xlkxtmul.json';
        break;

      default:
        path = 'https://assets10.lottiefiles.com/packages/lf20_j1adxtyb.json';
    }

    const container = document.getElementById('lottieContainer');

    if (!container) return;

    container.innerHTML = '';

    (window as any).lottie.loadAnimation({
      container,
      renderer: 'svg',
      loop: true,
      autoplay: true,
      path
    });
  }
}
